import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { recursiveFindByCode } from '@src/utils/tree';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportPeriodsStore } from '@stores/dictionaryStores/reportPeriodsStore';
import { reportSettingsStore } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { Filter } from './Filter';
import { Table } from './Table';
import { toTreeView } from './toTreeView';

import styles from './ReportSettings.module.scss';

const ReportSettingsComponent = () => {
  const {
    fetch,
    getReportSettingsLevel,
    reportSettings,
    clear,
    organizationCode,
    selectedKeys,
    expandedKeys,
    selectNode,
    setSelectNode,
    fullSize,
    setBreadcrumbs,
    setExpandedKeys,
    setSelectedKeys,
    toggleTemplateModal,
    filters,
  } = reportSettingsStore;
  const { push } = useHistory();
  const { groupInPeriods } = reportPeriodsStore;

  const isBack = useCheckFromDictionaryCard(RoutePath.DICTIONARY_REPORT_SETTINGS);

  const { CREATE: createReportSettings } = usePermission('CmReportSettings');
  const { VIEW: addTemplateView } = usePermission('CmReportSettingsAddTemplate');

  useAuditView('Настройка отчета');

  useEffect(() => {
    fetchReferences(['reportPeriods']);

    if (organizationCode) {
      fetch();
      getReportSettingsLevel();
    }
  }, [fetch, getReportSettingsLevel, organizationCode]);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const treeData = useMemo(() => {
    // отображаем только те группы, по которым есть настройка в Календаре отчетных форм
    const data = reportSettings.filter((item) => groupInPeriods.includes(item.reportGroupCode));

    return toTreeView(
      data.filter(
        (report) =>
          !(
            (filters.reportGroup && report.reportGroupCode !== filters.reportGroup) ||
            (filters.reportType && report.reportTypeCode !== filters.reportType)
          ),
      ),
    );
  }, [filters.reportGroup, filters.reportType, groupInPeriods, reportSettings]);

  const onSelect = useCallback(
    (_, { node, selected }) => {
      if (selected) {
        setSelectNode(node);
        setSelectedKeys([node.key]);

        const nodes = recursiveFindByCode(node.key, treeData);
        if (nodes) setBreadcrumbs(nodes.map((item) => item.title));
      }

      if (!selected) setSelectedKeys([]);
    },
    [setBreadcrumbs, setSelectNode, setSelectedKeys, treeData],
  );

  const onExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
    },
    [setExpandedKeys],
  );

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_REPORT_SETTINGS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const actions = useMemo(
    () => (
      <>
        {!selectNode && createReportSettings && (
          <Button onClick={onCreate} tooltip="Добавить">
            <Icon name="add" />
          </Button>
        )}
        {!selectNode && addTemplateView && (
          <Button onClick={toggleTemplateModal} key="add" tooltip="Добавить шаблон">
            <Icon name="addDocument" />
          </Button>
        )}
      </>
    ),
    [selectNode, createReportSettings, onCreate, addTemplateView, toggleTemplateModal],
  );

  return (
    <DesignLayout scrollEnable>
      <Filter />
      <>
        {organizationCode && (
          <div className={styles.container}>
            <Tree
              selectable
              treeData={treeData}
              onSelect={onSelect}
              className={cm(styles.tree, {
                [styles.withTable]: selectNode,
                [styles.fullSize]: fullSize,
              })}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              onExpand={onExpand}
              actions={actions}
            />
            <Table />
          </div>
        )}
      </>
    </DesignLayout>
  );
};

export const ReportSettings = observer(ReportSettingsComponent);
